import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Credential|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Credential,
	portal: 'SP',
};

export type SPCatalogsCredentialLocNames =
	| 'SPCatalogsCredentialTitlePage'
	| 'SPCatalogsCredentialLabelFilterByStatus'
	| 'SPCatalogsCredentialLabelFilterByGroup'
	| 'SPCatalogsCredentialLabelName'
	| 'SPCatalogsCredentialLabelNameAbbr'
	| 'SPCatalogsCredentialLabelStatus'
	| 'SPCatalogsCredentialLabelGroup'
	| 'SPCatalogsCredentialLabelActiveDate'
	| 'SPCatalogsCredentialLabelRetireDate'
	| 'SPCatalogsCredentialLabelLogo'
	| 'SPCatalogsCredentialLabelEcert'
	| 'SPCatalogsCredentialLabelBadgingEnabled'
	| 'SPCatalogsCredentialLabelTranscripts'
	| 'SPCatalogsCredentialLabelLogoEnabled'
	| 'SPCatalogsCredentialButtonCriteria'
	| 'SPCatalogsCredentialButtonMoreInfo'
	| 'SPCatalogsCredentialContentEligibilitiesInfo'
	| 'SPCatalogsCredentialLabelHiddenCertEligibility'
	| 'SPCatalogsCredentialLabelExternalId'
	| 'SPCatalogsCredentialLabelRecertificationLogic'
	| 'SPCatalogsCredentialLabelCEInRecertLogic';

export const spCatalogsCredentialLocs: LocDefs<SPCatalogsCredentialLocNames> = {
	SPCatalogsCredentialTitlePage: {
		defaultValue: 'Credentials',
		description: 'Page Title',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'Credential/Catalogs.Credentials.Title.CatalogPageTitle',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelFilterByStatus: {
		defaultValue: 'Filter by Status',
		description: 'Label for: Filter by Status.',
		displayPath: `${pathStart}Filter by Status`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.FieldLabel.FilterStatus',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelFilterByGroup: {
		defaultValue: 'Filter by Group',
		description: 'Label for: Filter by Group.',
		displayPath: `${pathStart}Filter by Group`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.FieldLabel.FilterGroup',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelName: {
		defaultValue: 'Name',
		description: 'Column heading for: Name.',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.ColumnHeading.Name',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelNameAbbr: {
		defaultValue: 'Name Abbr.',
		description: 'Column heading for: Name Abbr.',
		displayPath: `${pathStart}Name Abbr.`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.ColumnHeading.NameAbbr',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelStatus: {
		defaultValue: 'Status',
		description: 'Column heading for: Status.',
		displayPath: `${pathStart}Status`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.ColumnHeading.Status',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelGroup: {
		defaultValue: 'Group',
		description: 'Column heading for: Group.',
		displayPath: `${pathStart}Group`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.ColumnHeading.Group',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelActiveDate: {
		defaultValue: 'Active Date',
		description: 'Column heading for: Active Date.',
		displayPath: `${pathStart}Active Date`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.ColumnHeading.ActiveDate',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelRetireDate: {
		defaultValue: 'Retire Date',
		description: 'Column heading for: Retire Date.',
		displayPath: `${pathStart}Retire Date`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.ColumnHeading.RetireDate',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelLogo: {
		defaultValue: 'Logo',
		description: 'Column heading for: Logo.',
		displayPath: `${pathStart}Logo`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.ColumnHeading.Logo',
		shared: false,
		htmlAllowed: false,
		feature: HubFeature.CredentialLogoDownload,
		portal: 'SP',
	},
	SPCatalogsCredentialLabelEcert: {
		defaultValue: 'ECert',
		description: 'Column heading for: ECert.',
		displayPath: `${pathStart}ECert`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.ColumnHeading.Ecert',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelBadgingEnabled: {
		defaultValue: 'Badging Enabled',
		description: 'Column heading for: Badging Enabled.',
		displayPath: `${pathStart}Badging Enabled`,
		displayType: 'Label',
		legacyName: 'Share.CertMetricsBadging/Catalogs.Credentials.ColumnHeading.BadgingEnabled',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelTranscripts: {
		defaultValue: 'Transcripts',
		description: 'Column heading for: Transcripts.',
		displayPath: `${pathStart}Transcripts`,
		displayType: 'Label',
		legacyName: 'Credential/Catalogs.Credentials.ColumnHeading.Transcripts',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelLogoEnabled: {
		defaultValue: 'Logo Enabled',
		description: 'Column heading for: Logo Enabled.',
		displayPath: `${pathStart}Logo Enabled`,
		displayType: 'Label',
		shared: false,
		htmlAllowed: false,
		feature: HubFeature.CredentialLogoDownload,
		portal: 'SP',
	},
	SPCatalogsCredentialButtonCriteria: {
		defaultValue: 'Criteria',
		description: 'Button for: Criteria.',
		displayPath: `${pathStart}Criteria`,
		displayType: 'Button',
		legacyName: 'Credential/Catalogs.Credentials.Button.Criteria',
		...sharedProperties,
	},
	SPCatalogsCredentialButtonMoreInfo: {
		defaultValue: 'More Information',
		description: 'Button for: More Information.',
		displayPath: `${pathStart}More Information`,
		displayType: 'Button',
		legacyName: 'Exam/Candidates.ProgramHistory.Exams.Title.InfoBug',
		...sharedProperties,
	},
	SPCatalogsCredentialContentEligibilitiesInfo: {
		defaultValue: 'This Cert track is hidden from the Candidate.',
		description: 'Content shown for eligibilities.',
		displayPath: `${pathStart}Cert Track Hidden`,
		displayType: 'Content',
		legacyName: 'Credential/Catalogs.Credentials.Content.EligibilitiesInfo',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelHiddenCertEligibility: {
		defaultValue: 'Hidden Cert Eligibility',
		description: 'Column heading for: Hidden Cert Eligibility.',
		displayPath: `${pathStart}Hidden Cert Eligibility`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelExternalId: {
		defaultValue: 'External ID',
		description: 'Column heading for: External ID.',
		displayPath: `${pathStart}External ID`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPCatalogsCredentialLabelRecertificationLogic: {
		defaultValue: 'Recertification Logic',
		description: 'Column heading for: Recertification Logic',
		displayPath: `${pathStart}Recertification Logic`,
		displayType: 'Label',
		shared: false,
		htmlAllowed: false,
		feature: HubFeature.CredentialRecertLogic,
		portal: 'SP',
	},
	SPCatalogsCredentialLabelCEInRecertLogic: {
		defaultValue: 'CE In Recert Logic',
		description: 'Column heading for: CE In Recert Logic',
		displayPath: `${pathStart}CE In Recert Logic`,
		displayType: 'Label',
		shared: false,
		htmlAllowed: false,
		feature: HubFeature.CredentialContinuingEducation,
		portal: 'SP',
	},
};

export type SPCatalogsCredentialLocType = LocMap<SPCatalogsCredentialLocNames>;
